import React, { useRef, useState, useEffect } from 'react';
import { Brain, Pen, Code, Rocket, TrendingUp } from 'lucide-react';
import { spacing, shadows, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

const phaseIcons = {
  think: Brain,
  design: Pen,
  develop: Code,
  launch: Rocket,
  scale: TrendingUp
};

/**
 * PresetPhaseSelector Component
 *
 * Animated tabs with sliding indicator.
 */
const PresetPhaseSelector = ({ currentPhase, onPhaseChange, phases, phaseKeys }) => {
  const { colors } = useTheme();
  const containerRef = useRef(null);
  const buttonRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const activeButton = buttonRefs.current[currentPhase];
    if (activeButton && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setIndicatorStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width
      });
    }
  }, [currentPhase]);

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      zIndex: 10,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      background: colors.background,
      paddingTop: spacing.md,
      paddingBottom: spacing.md,
      marginTop: `-${spacing.xl}`,
      marginBottom: spacing.lg
    }}>
      <div
        ref={containerRef}
        style={{
          display: 'inline-flex',
          position: 'relative',
          background: colors.backgroundPanel,
          borderRadius: borderRadius.xl,
          padding: '4px',
          border: `1px solid ${colors.border}`,
          boxShadow: shadows.xs
        }}
      >
      {/* Sliding Indicator */}
      <div
        style={{
          position: 'absolute',
          top: '4px',
          left: indicatorStyle.left,
          width: indicatorStyle.width,
          height: 'calc(100% - 8px)',
          background: colors.backgroundOffset,
          borderRadius: borderRadius.lg,
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none'
        }}
      />

      {phaseKeys.map((phase) => {
        const Icon = phaseIcons[phase];
        const isActive = currentPhase === phase;

        return (
          <button
            key={phase}
            ref={(el) => (buttonRefs.current[phase] = el)}
            onClick={() => onPhaseChange(phase)}
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.xs,
              fontSize: '0.9375rem',
              fontWeight: 500,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'color 0.2s',
              padding: `${spacing.sm} ${spacing.lg}`,
              color: isActive ? colors.foreground : colors.foregroundSecondary,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              borderRadius: borderRadius.lg,
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = colors.foreground;
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = colors.foregroundSecondary;
              }
            }}
          >
            <Icon size={18} />
            <span>{phases[phase].name}</span>
          </button>
        );
      })}
      </div>
    </div>
  );
};

export default PresetPhaseSelector;
